import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { React, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { AuthService, Alert, Spinner } from '../../services';

import {
  UPPERCASE_REGEX,
  NUMBER_REGEX,
  LENGTH_REGEX,
  SPECIAL_CHARS_REGEX,
} from '../../utils/validationsRules';
import ButtonLoader from '../../components/ButtonLoader';
import { CloseEyeIcon, OpenEyeIcon } from '../../components/icons/IconProvider';
import AuthHeaderTextComponent from '../../components/auth/AuthHeaderTextComponent';
import FormInput from '../../components/FormInput';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [passwordShown, setPasswordShown] = useState(false);
  const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
  const [isLoader, setLoader] = useState(false);

  const baseClasses =
    'text-[#0B243F] text-lg font-semibold rounded-md px-5 leading-normal py-3 flex flex-col justify-center items-center w-full';
  const activeClasses =
    'bg-[#A7D760] hover:bg-[#97C05B] focus:bg-[#A7D760] active:bg-[#8DB158] hover:transition-all';
  const disabledClasses = 'bg-gray-400 cursor-not-allowed';

  const rules = [
    {
      label: 'Include both lower and upper case letters.',
      pattern: UPPERCASE_REGEX,
    },
    { label: 'Include at least one number.', pattern: NUMBER_REGEX },
    {
      label: 'Include at least 1 special character.',
      pattern: SPECIAL_CHARS_REGEX,
    },
    { label: 'be at least 8 characters long.', pattern: LENGTH_REGEX },
  ];

  const PASSWORD_REGEX =
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[-'/`~!#*$@_%+=.,^&(){}[\]|;:"<>?\\])[A-Za-z\d@$!%*#?&-'/`~!#*$@_%+=.,^&(){}[\]|;:"<>?\\]{8,}$/;

  const schema = Yup.object({
    password: Yup.string()
      .required('New Password is required')
      .matches(PASSWORD_REGEX, 'Password is invalid'),
    confirmPassword: Yup.string()
      .required('Confirm Password is Required')
      .oneOf(
        [Yup.ref('password')],
        'New Password and Confirm Password should be the same',
      ),
  }).required();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const passwordValue = watch('password', '');

  useEffect(() => {
    verifyToken();
  }, []);

  const verifyToken = async () => {
    let responseToken = await AuthService.verifyToken(token);
    if (responseToken.status === 'failure') {
      Spinner.hide();
      Alert.error(responseToken.message);
      navigate('/login');
    }
  };

  const onResetPassword = async (_input) => {
    try {
      setLoader(true);
      const data = {
        password: _input.password,
        confirm_password: _input.confirmPassword,
        token: token,
      };
      let response = await AuthService.resetPassword(data);
      if (response.status === 'failure') {
        setLoader(false);
        Alert.error(response.message);
        return false;
      }

      setLoader(false);
      Alert.success(response.message);
      navigate('/login');
    } catch (error) {
      setLoader(false);
      Alert.error(error.message);
      return false;
    }
  };

  const togglePasswordVisiblity = (type) => {
    if (type === 'password') {
      setPasswordShown(!passwordShown);
    } else {
      setConfirmPasswordShown(!confirmPasswordShown);
    }
  };

  const isPasswordValid = passwordValue && PASSWORD_REGEX.test(passwordValue);

  const isDisabled = isLoader || !isPasswordValid || !isValid;

  const buttonClasses = isDisabled
    ? `${baseClasses} ${disabledClasses}`
    : `${baseClasses} ${activeClasses}`;

  return (
    <div className="hasBGSignUp">
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-full md:w-96 bg-white px-6 py-10 rounded-md m-6">
          <AuthHeaderTextComponent title={'Reset Password'} desc={''} />
          <form onSubmit={handleSubmit(onResetPassword)}>
            <div className="relative mb-2 mt-4">
              <FormInput
                label="New Password"
                name="password"
                type={passwordShown ? 'text' : 'password'}
                placeholder="New Password"
                register={register}
                errors={errors}
                className={
                  errors.password?.message
                    ? 'mb-1 dark:text-gray-900 border-red-500 focus-within:outline-red-500'
                    : 'mb-1 dark:text-gray-900'
                }
              />

              <Link to="" onClick={() => togglePasswordVisiblity('password')}>
                <div className="resetpassword-password">
                  {passwordShown ? <OpenEyeIcon /> : <CloseEyeIcon />}
                </div>
              </Link>
            </div>
            <div className="relative mb-2 mt-4">
              <FormInput
                label="Confirm New Password"
                name="confirmPassword"
                type={confirmPasswordShown ? 'text' : 'password'}
                placeholder="Confirm New Password"
                register={register}
                errors={errors}
                className={
                  errors.confirmPassword?.message
                    ? 'mb-1 dark:text-gray-900 border-red-500 focus-within:outline-red-500'
                    : 'mb-1 dark:text-gray-900'
                }
              />

              <Link
                to=""
                onClick={() => togglePasswordVisiblity('confirmPassword')}
              >
                <div className="resetpassword-password">
                  {confirmPasswordShown ? <OpenEyeIcon /> : <CloseEyeIcon />}
                </div>
              </Link>
            </div>

            <div
              className={
                isPasswordValid
                  ? 'p-2'
                  : 'flex flex-wrap gap-x-3 p-2 border border-red-500'
              }
            >
              <p className="font-semibold">Your password need to:</p>
              {rules.map((rule, index) => {
                const isRuleMet =
                  passwordValue && passwordValue.match(rule.pattern);
                const cn = isRuleMet
                  ? 'text-green-500 text-[13px]'
                  : passwordValue
                    ? 'text-red-500 text-[13px]'
                    : 'text-[13px]';
                return (
                  <p key={index} className={cn}>
                    {isRuleMet ? '✔ ' : passwordValue ? '× ' : '• '}
                    {rule.label}
                  </p>
                );
              })}
            </div>
            <div className="mt-6">
              <button
                type="submit"
                disabled={isDisabled}
                className={buttonClasses}
              >
                {isLoader ? (
                  <ButtonLoader title={'Processing'} />
                ) : (
                  'Reset Password'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
